import { useCallback, useEffect, useRef, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'

import { getSocket } from '@/core/api/socket'
import { useGetConsultationQuery } from '@/core/services/consultation'
import { useRecordTranscriptionConsentMutation, useRequestTranscriptionConsentMutation } from '@/core/services/transcription'
import { SocketEvent } from '@/core/types/socket-event'

/** `granted` is the only state in which the server opens a live transcription session. */
export type TranscriptionConsentStatus = 'unknown' | 'waiting' | 'granted' | 'refused'

export type UseTranscriptionConsentOptions = {
  consultationId?: string
  /** Called once the patient accepts a request made from this screen (auto-starts the transcription). */
  onGranted?: () => void
}

export type UseTranscriptionConsentResult = {
  status: TranscriptionConsentStatus
  /** Sends the consent request to the patient app, where the patient answers on their own screen. */
  requestFromPatient: () => Promise<void>
  /** Records the patient's verbal answer, given in front of the practitioner. */
  recordVerbalAnswer: (accepted: boolean) => Promise<void>
  isRecording: boolean
}

type ConsentResponse = { consultationId: string; accepted: boolean }

/**
 * Patient consent to live transcription, shared with the recording consent flow:
 * `consultation.patientConsent.recordingConsent` is the source of truth (server-enforced).
 */
export function useTranscriptionConsent({ consultationId, onGranted }: UseTranscriptionConsentOptions): UseTranscriptionConsentResult {
  const { data: consultation, refetch } = useGetConsultationQuery(consultationId ?? skipToken)
  const [recordConsent, { isLoading: isRecording }] = useRecordTranscriptionConsentMutation()
  const [requestConsent, { isLoading: isRequesting }] = useRequestTranscriptionConsentMutation()
  const [requested, setRequested] = useState(false)
  const requestedRef = useRef(false)
  const onGrantedRef = useRef(onGranted)
  onGrantedRef.current = onGranted

  const consent = consultation?.patientConsent?.recordingConsent
  const pending = requested || !!consultation?.recordingConsentPending
  const status: TranscriptionConsentStatus = consent === true ? 'granted' : pending ? 'waiting' : consent === false ? 'refused' : 'unknown'

  // The patient's answer (from the patient app, or a colleague recording a verbal answer).
  useEffect(() => {
    if (!consultationId) return
    let disposed = false
    let socket: Awaited<ReturnType<typeof getSocket>> | undefined
    const listener = (data: ConsentResponse) => {
      if (data.consultationId !== consultationId) return
      const askedHere = requestedRef.current
      requestedRef.current = false
      setRequested(false)
      refetch()
      if (data.accepted && askedHere) onGrantedRef.current?.()
    }
    getSocket().then((s) => {
      if (disposed) return
      socket = s
      socket.on(SocketEvent.RECORDING_CONSENT_RESPONSED, listener)
    })
    return () => {
      disposed = true
      socket?.off(SocketEvent.RECORDING_CONSENT_RESPONSED, listener)
    }
  }, [consultationId, refetch])

  const requestFromPatient = useCallback(async () => {
    if (!consultationId) return
    requestedRef.current = true
    setRequested(true)
    try {
      await requestConsent({ consultationId }).unwrap()
    } catch (error) {
      requestedRef.current = false
      setRequested(false)
      throw error
    }
  }, [consultationId, requestConsent])

  const recordVerbalAnswer = useCallback(
    async (accepted: boolean) => {
      if (!consultationId) return
      requestedRef.current = false
      setRequested(false)
      await recordConsent({ consultationId, accepted }).unwrap()
      if (accepted) onGrantedRef.current?.()
    },
    [consultationId, recordConsent]
  )

  return { status, requestFromPatient, recordVerbalAnswer, isRecording: isRecording || isRequesting }
}
